/**
 * MVP mode — permanently ON (Jack 2026-08-09: "the MVP experience is now THE
 * product divisions will use"). The dual-mode resolution (?mvp=0 / ?full=1 /
 * localStorage "mvpMode") and the setMvpMode() switch were removed in the
 * MVP-only cutover; there is no full/original mode and no escape hatch.
 *
 * What the (always-on) MVP contract enforces, each where it lives:
 *   - Step 3 shows the 7 Send Scope fields (Seg3 kept on disk, never rendered)
 *   - the template library is the live SFMC templates (default: Coming Soon — see
 *     MVP_TPL_DEFAULT; it was the alias "Tease - Coming Soon" until 2026-08-24)
 *   - Creative Studio: no blank canvas, no left palette (customize/move only),
 *     block add restricted to MVP_BLOCKS
 *   - duplicate-a-job limited to jobs built from the 5 templates (MVP_JOB_RE)
 *   - hero output forced to single (no GIF/carousel)
 *
 * MVP_ON stays exported as a constant so historical call sites don't churn.
 */

import { echTemplateNamesAndAliases } from "./echTemplates";

export const MVP_ON = true as const;

/** The 5 MVP templates (monolith NURTURE_TEMPLATES when MVP_ON) */
export const MVP_TEMPLATES = [
  "Tease - Coming Soon",
  "Launch - Sales Just Launched",
  "Launch - Grand Opening Invite",
  "Sustain - Actively Selling",
  "Tease - Join the VIP List",
];
/**
 * ANY LOADED ECH_TPL_ TEMPLATE CAN BE DUPLICATED (2026-10-06, Layne: "duplicating allowed for
 * any ECH_TPL_ template, instead of a per-template list"). A duplicated job is matched against
 * the five originals plus every template the block library actually holds — its canonical name
 * and its aliases — so a template Layne adds needs no code change, and prod never pre-picks a
 * template its library does not have (a `_TEST`-only template is simply not loaded there).
 */
export function mvpTemplateChoices(resolve: (name: string) => string | null): string[] {
  const out: string[] = [];
  const add = (n: string) => { if (n && out.indexOf(n) < 0) out.push(n); };
  MVP_TEMPLATES.forEach(add);
  echTemplateNamesAndAliases().filter((n) => !!resolve(n)).forEach(add);
  return out;
}

/** true when the text names a loaded template (canonical name or alias), whole-phrase */
function namesLoadedTemplate(text: string): boolean {
  const t = text.toLowerCase();
  return echTemplateNamesAndAliases().some((n) => {
    const k = String(n || "").trim().toLowerCase();
    return k.length >= 6 && t.indexOf(k) > -1;
  });
}
/**
 * THE DEFAULT IS A LIVE SFMC TEMPLATE NAME, NOT A LEGACY ALIAS (2026-08-24, per Jack:
 * *"remove the old we should only be using the sfmc templates"*).
 *
 * It used to be `"Tease - Coming Soon"`, the gallery name. That name is only an ALIAS of
 * the SFMC template `Coming Soon` (echTemplates ECH_ALIAS), and it is ALSO a real row in
 * `/templates.json` — so on the app's default template the stored catalog HTML answered
 * the preview rail and the division saw the pre-ECH Stensul design instead of the
 * block-built one (FormView's `/template-html` fetch, measured in Chrome 8/24: zero
 * `ECH_*` blocks in the rail, `st-email-body` present).
 *
 * `Coming Soon` is the canonical `ECH_TPL_ComingSoon` name, so the rail assembles from
 * Layne's blocks on first paint and no catalog row can outrank it. The alias stays
 * registered — old saved jobs and favorites still resolve through it.
 */
export const MVP_TPL_DEFAULT = "ECH - Coming Soon";
/* 2026-10-19 (Layne): renamed with the other nine to match Katy's Airtable names. Until a
   library carries the new name, `echCanonicalName` maps it to the old "Coming Soon", and
   FormView rewrites the form's value to whichever name the loaded library uses. */

/**
 * ⛔ MVP_BLOCKS NO LONGER OPENS THE PALETTE (2026-08-20, per Jack: *"remove any that's
 * ours — why is it even there… remove everything not in SFMC!"*).
 *
 * WHAT WENT WRONG. This list let eleven of the app's OWN hand-built blocks into the
 * insert menu next to Layne's. They look like real blocks and they are not in Content
 * Builder, so a division could build an email out of markup SFMC has never seen. Jack
 * hit it directly: he opened the insert menu, picked "Listing Table", and got OUR block
 * — a single free-text `Sq ft / Beds / Baths` field and a Price box showing "View Home",
 * because our invented `plan | specs | price | view` string only had three parts.
 *
 * Layne's equivalent was in the same menu all along, listed as "Community Home Table"
 * (`ECH_ListingTable`), and it declares 73 fields — Homesite, Plan, SqFt, Beds, Baths,
 * Price and Url separately for each of ten homes, SqFt/Beds/Baths typed as numbers,
 * plus `collection1Name` and `viewLabel`. Everything the panel was being asked to grow,
 * her block already had.
 *
 * So the palette is now ECH-only. The list is KEPT — the legacy renderers are still
 * needed to open an old saved build or a favorite that contains them, and deleting the
 * registry entries would break those documents. It just no longer offers them for NEW
 * work. Do not widen this back to "or MVP_BLOCKS.indexOf(t) > -1".
 */
export const MVP_BLOCKS = ["logo", "hero", "headline", "body", "cta", "community", "listing", "map", "isc", "footer", "ftfooter"];

/**
 * A block may be INSERTED only if it exists in Content Builder.
 *
 * `sfmc_<contentId>` is the ECH key space — the ids come from SFMC at runtime, so this
 * test is exactly "did we pull this block from Layne's folder".
 */
export function mvpAllowBlock(t: string): boolean {
  return /^sfmc_/.test(t);
}

/** Duplicate-a-job filter (monolith MVP_JOB_RE / __mvpJobAllow) */
/* the original five's words; every other template is allowed by name through
   `namesLoadedTemplate` below (2026-10-06) */
const MVP_JOB_RE = /coming soon|sales just launched|just launched|grand opening|actively selling|vip list/;
/** favorites filter (monolith index.html L4998/L5013/L5054): the ★ RMT / Division
 *  Favorites browser only lists favorites BUILT FROM the 5 templates — same regex,
 *  matched on the favorite's name. */
export function mvpFavAllow(name: string): boolean {
  const n = String(name || "");
  return MVP_JOB_RE.test(n.toLowerCase()) || namesLoadedTemplate(n);
}
/**
 * The CONTENT test for a Studio-build favorite (Jack 2026-08-21: "can only
 * save/show templates related to the MVP set up — not these"). The name regex
 * above passed two old test saves literally named "…Coming Soon…" whose saved
 * HTML was an FTHB-webinar email and a rates email — a name is not the build.
 * An MVP build's export carries the ECH manifest stamp (services/manifest.ts,
 * `<!--ECH-MANIFEST:…-->` before </body>), so the stamp IS the provenance:
 * present → built here from the MVP flow; absent → legacy/bespoke, hidden.
 */
export function mvpFavContentAllow(html: string): boolean {
  return String(html || "").indexOf("ECH-MANIFEST") > -1;
}
export function mvpJobAllow(j: Record<string, unknown> | null | undefined): boolean {
  const t = (String((j && j.project) || "") + " " + String((j && j.subject) || "") + " "
    + String((j && j.initiative) || "") + " " + String((j && (j.category || (j as { cat?: unknown }).cat)) || "")
    + " " + String((j && (j.template || (j as { nurtureTemplate?: unknown }).nurtureTemplate)) || "")).toLowerCase();
  return MVP_JOB_RE.test(t) || namesLoadedTemplate(t);
}
